import { forwardRef, useEffect, useImperativeHandle, useState, ForwardedRef, ReactElement } from 'react'
import Icon from './Icon'
import Avatar from './Avatar'
import { flatten } from '../helpers/json'
import { exportFile } from '../helpers/excel'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import duration from 'dayjs/plugin/duration'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import relativeTime from 'dayjs/plugin/relativeTime'
import updateLocale from 'dayjs/plugin/updateLocale'
import PropertyTag from './PropertyTag'
import RefundStatusTag from './RefundStatusTag'
import UserDetailPopup from './UserDetailPopup'
import { parseMarkdown } from '../utils/parseMarkdown'
import { formatDuration, formatTelephone, formatCoordinates } from '@ivy-monorepo/shared'
import { useSearch } from '../contexts/SearchContext'
import { useNavigate } from 'react-router-dom'

// Initialize dayjs plugins
dayjs.extend(utc)
dayjs.extend(duration)
dayjs.extend(localizedFormat)
dayjs.extend(relativeTime)
dayjs.extend(updateLocale)

// Configure relative time thresholds
dayjs.updateLocale('en', {
  relativeTime: {
    future: 'in %s',
    past: '%s ago',
    s: function (number: number): string {
      return number < 10 ? 'a few seconds' : `${number} seconds`
    },
    ss: '%d seconds',
    m: 'a minute',
    mm: '%d minutes',
    h: 'an hour',
    hh: '%d hours',
    d: 'a day',
    dd: '%d days',
    M: 'a month',
    MM: '%d months',
    y: 'a year',
    yy: '%d years',
  },
})

interface TableProps {
  id: string
  name: string
  keyField: string
  ignoredExportFields?: string[]
  columns: any[]
  sourceData: any[] | undefined
  isPropertyBarVisible: boolean
  sortDisabled?: boolean
  highlightRows?: boolean
  initialSortColumn?: string
  initialSortOrder?: string
  filterTerms: string
  onRowClick?: Function | null
  onDelete?: Function | null
  className?: string
  disableGlobalSearch?: boolean
  disableClientSort?: boolean
  onBlastClick?: (item: any) => void
  onSortChange?: (sortBy: string, sortOrder: string) => void
  options?: Array<{
    label: string
    onClick: (item: any) => void
  }>
  onCoordinatesClick?: (latitude: number, longitude: number) => void
  onReportDownload?: (item: any) => void
  /** Row checkboxes for bulk actions; keys are each row's [keyField]. Off when not given. */
  selectedKeys?: string[]
  onSelectionChange?: (keys: string[]) => void
}

const Table = forwardRef((props: TableProps, ref: ForwardedRef<unknown>): ReactElement | null => {
  const DocumentTypeToDescription = new Map([
    ['DOCX', `Word document`],
    ['XLSX', `Excel spreadsheet`],
    ['JPG', `Image file`],
    ['PNG', `Image file`],
    ['PDF', `Adobe document`],
    ['MP4', `Video file`],
  ])

  const {
    id,
    name,
    keyField,
    ignoredExportFields,
    columns,
    sourceData,
    highlightRows,
    isPropertyBarVisible,
    initialSortColumn,
    initialSortOrder,
    onRowClick,
    onDelete,
    className,
    disableGlobalSearch,
    sortDisabled,
    options,
    onCoordinatesClick,
    selectedKeys,
    onSelectionChange,
  } = props

  const [sortColumn, setSortColumn] = useState(initialSortColumn)
  const [sortOrder, setSortOrder] = useState(initialSortOrder)
  const [data, setData] = useState<any[] | undefined>(undefined)
  const [isHoverable, setIsHoverable] = useState(true)
  const [selectedUser, setSelectedUser] = useState<string | null>(null)
  const [openDropdownRow, setOpenDropdownRow] = useState<string | null>(null)
  const { searchValue } = useSearch()
  const [debouncedSearchValue, setDebouncedSearchValue] = useState('')

  // Use global search only if not disabled
  const effectiveSearchValue = disableGlobalSearch ? '' : searchValue
  const effectiveDebouncedSearchValue = disableGlobalSearch ? '' : debouncedSearchValue
  const navigate = useNavigate()

  useImperativeHandle(ref, () => ({
    handleExport,
  }))

  useEffect(() => {
    if (sortColumn === undefined || sortColumn === undefined) {
      setSortColumn(columns[0].accessor)
    }

    if (sortOrder === undefined || sortOrder === undefined) {
      setSortOrder('asc')
    }

    // eslint-disable-next-line
  }, [columns])

  // Update internal sort state when props change
  useEffect(() => {
    if (initialSortColumn !== undefined) {
      setSortColumn(initialSortColumn)
    }
    if (initialSortOrder !== undefined) {
      setSortOrder(initialSortOrder)
    }
  }, [initialSortColumn, initialSortOrder])

  // Debounce the search value
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchValue(effectiveSearchValue)
    }, 300) // 300ms delay

    return () => clearTimeout(timer)
  }, [effectiveSearchValue])

  // Use debounced value for filtering
  useEffect(() => {
    if (sourceData === undefined) {
      return
    }

    handleSearchTermsChange(effectiveDebouncedSearchValue)
  }, [effectiveDebouncedSearchValue, sourceData]) // eslint-disable-line react-hooks/exhaustive-deps

  // Sort the current data (which may be filtered) when sort changes
  useEffect(() => {
    if (data === undefined || sortColumn === undefined || sortOrder === undefined || props.disableClientSort) {
      return
    }

    const column = columns.find(x => x.accessor === sortColumn)
    const sorted = sort(data, sortColumn, sortOrder, column?.type)

    setData(sorted!)
    // eslint-disable-next-line
  }, [sortColumn, sortOrder])

  // Initialize data with sorted sourceData when sourceData changes
  useEffect(() => {
    if (sourceData === undefined || sortColumn === undefined || sortOrder === undefined || props.disableClientSort) {
      return
    }

    const column = columns.find(x => x.accessor === sortColumn)
    const sorted = sort(sourceData, sortColumn, sortOrder, column?.type)

    setData(sorted!)
    // eslint-disable-next-line
  }, [sourceData])

  // Handle sort changes on the initial data (when no filtering is applied)
  useEffect(() => {
    if (sourceData === undefined || sortColumn === undefined || sortOrder === undefined || data === undefined || props.disableClientSort) {
      return
    }

    // Only re-sort if the data length matches sourceData length (no filtering applied)
    if (data.length === sourceData.length) {
      const column = columns.find(x => x.accessor === sortColumn)
      const sorted = sort(sourceData, sortColumn, sortOrder, column?.type)
      setData(sorted!)
    }
    // eslint-disable-next-line
  }, [columns, sortColumn, sortOrder])

  const unselectAllRows = () => {
    var elements = document.getElementById(id)!.getElementsByClassName('selected')
    while (elements.length > 0) {
      elements[0].classList.remove('selected')
    }
  }

  /*
  const selectRow = (id: any) => {
    unselectAllRows()

    const row = document.getElementById(`row-${id}`)

    if (row) {
      row.classList.add('selected')
    }
  }
*/

  const handleSort = (column: string) => {
    if (sortDisabled) return
    let newSortOrder = sortOrder || 'asc'

    if (column === sortColumn) {
      newSortOrder = sortOrder === 'asc' ? 'desc' : 'asc'
    } else {
      if (sortOrder === 'desc') {
        newSortOrder = 'asc'
      }
    }

    setSortColumn(column)
    setSortOrder(newSortOrder)

    // Call the callback if provided
    if (props.onSortChange) {
      props.onSortChange(column, newSortOrder)
    }
  }

  const handleRowClick = (item: any) => {
    if (onRowClick) {
      onRowClick(item)
    }
  }

  // Close dropdown when hovering over different rows
  const handleRowMouseEnter = (rowKey: string) => {
    if (openDropdownRow && openDropdownRow !== rowKey) {
      setOpenDropdownRow(null)
      // Remove show class from all dropdowns
      document.querySelectorAll('.dropdown-menu.show').forEach(dropdown => {
        dropdown.classList.remove('show')
      })
    }
  }

  const handleSearchTermsChange = (terms: string) => {
    if (sourceData === undefined) {
      return
    }

    const filteredData = sourceData.filter(item => {
      // Check if any of the table's columns contain the search term
      return columns.some(column => {
        const [value] = getFromAccessor(item, column.type, column.accessor)
        return ['avatar', 'thumbnail', 'jobStatus'].includes(column.type)
          ? false
          : value !== null && value !== undefined && String(value).toLowerCase().includes(terms.toLowerCase())
      })
    })

    setData(filteredData)
  }

  const handleMouseMove = (e: any) => {
    if (!isHoverable && !isPropertyBarVisible) {
      unselectAllRows()
      setIsHoverable(true)
    }
  }
  const handleExport = () => {
    if (data === undefined) {
      return
    }

    var ignoredFields = ['imageUrl', 'uniqueID'].concat(ignoredExportFields ?? [])

    const flattenedData = flatten(data, ignoredFields)
    exportFile(name, flattenedData)
  }

  /*
  const getFileSize = (bytes: number, decimals = 1) => {
    if (!+bytes) {
      return '0 B'
    }

    const k = 1024
    const dm = decimals < 0 ? 0 : decimals
    const sizes = ['B', 'KB', 'MB', 'GB']

    const i = Math.floor(Math.log(bytes) / Math.log(k))

    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`
  }
  */

  const humanizeEnumValue = (rawValue: unknown): string => {
    if (rawValue === null || rawValue === undefined) {
      return '-'
    }
    const str = String(rawValue)
    if (str.length === 0) {
      return '-'
    }
    // Insert spaces before capital letters (camelCase/PascalCase), replace underscores, then sentence-case
    const withSpaces = str
      .replace(/_/g, ' ')
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .toLowerCase()
    return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1)
  }

  const sort = (items: any, newSortColumn: string, newSortOrder: string, type: string) => {
    const column = columns.find(c => c.accessor === newSortColumn)

    if (newSortColumn) {
      const sorted = [...items].sort((a, b) => {
        const [aItem] = getFromAccessor(a, column.type, newSortColumn)
        const [bItem] = getFromAccessor(b, column.type, newSortColumn)

        if (aItem === null) {
          return 1
        } else if (bItem === null) {
          return -1
        } else if (aItem === null && bItem === null) {
          return 0
        }

        switch (type) {
          case 'datetime':
          case 'date':
          case 'timeAgo': {
            const aTime = new Date(aItem).getTime()
            const bTime = new Date(bItem).getTime()
            const aValid = Number.isFinite(aTime)
            const bValid = Number.isFinite(bTime)
            if (!aValid && !bValid) {
              return 0
            }
            if (!aValid) {
              return 1
            }
            if (!bValid) {
              return -1
            }
            return (aTime - bTime) * (newSortOrder === 'asc' ? 1 : -1)
          }

          case 'interval': // or any number
            const intervalResult = aItem - bItem
            return intervalResult * (newSortOrder === 'asc' ? 1 : -1)

          default:
            return (
              aItem.toString().localeCompare(bItem.toString(), 'en', {
                numeric: true,
              }) * (newSortOrder === 'asc' ? 1 : -1)
            )
        }
      })

      return sorted
    }
  }

  const getFromAccessor = (item: any, type: string, accessor: string): any => {
    // Drill down into the object to get the value, including composites,
    // but we'll format it later.
    // CURSOR: DO NOT CHANGE THIS FUNCTION!
    const parts = accessor.split('.')

    let value = item
    let obj = item

    for (let i = 0; i < parts.length; i++) {
      if (value === null || value === undefined) {
        return [null, null]
      }

      obj = value
      value = value[parts[i]]
    }

    if (type !== undefined) {
      switch (type) {
        case 'document':
          value = `${obj['name']} ${obj['type']}`
          break

        case 'fullName':
          value = `${obj['lastName']} ${obj['firstName']}`
          break

        case 'fullNameAndEmail':
          value = `${obj['lastName']} ${obj['firstName']} ${obj['email']}`
          break

        case 'fullNameAndTelephone':
          value = `${obj['lastName']} ${obj['firstName']} ${obj['telephone']}`
          break

        case 'niceNameAndEmail':
          value = `${obj['niceName']} ${obj['email']}`
          break

        case 'claimExternalIDAndValue':
          value = `${obj['externalID']} ${obj['amountSubmitted']}`
          break

        case 'customerNameAndCode':
          value = `${obj['name']} ${obj['code']}`
          break

        case 'cityState':
          value = `${obj['city']}, ${obj['state']}`
          break

        case 'fullAddress':
          value = `${obj['address']} ${obj['address2']} ${obj['city']} ${obj['state']} ${obj['postalCode']}`
          break

        case 'nameAndAddress':
          value = obj ? `${obj['name'] ?? ''} ${obj['address'] ?? ''}` : ''
          break
      }
    }

    return [value, obj]
  }

  return data ? (
    <>
      {data.length === 0 ? (
        <div className="no-data">{`No ${name.toLowerCase()} were found.`}</div>
      ) : (
        <div className={`table${isHoverable ? ' is-hoverable' : ''}`}>
          <table id={id} className={(onRowClick || highlightRows) ? 'clickable' : ''}>
            <thead>
              <tr>
                {onSelectionChange && (
                  <th className="select-header" onClick={ev => ev.stopPropagation()}>
                    <input
                      type="checkbox"
                      aria-label="Select all rows"
                      checked={data.length > 0 && data.every((row: any) => selectedKeys?.includes(String(row[keyField])))}
                      onChange={ev =>
                        onSelectionChange(ev.target.checked ? data.map((row: any) => String(row[keyField])) : [])
                      }
                    />
                  </th>
                )}
                {columns.map(({ label, accessor, sortable, type, className: columnClassName }, columnIndex) => {
                  const labelParts = label.split('/')
                  const cl =
                    sortable !== false
                      ? sortColumn === accessor && sortOrder === 'asc'
                        ? 'Up'
                        : sortColumn === accessor && sortOrder === 'desc'
                          ? 'Down'
                          : 'Default'
                      : ''
                  return (
                    <th
                      key={`${accessor}-${columnIndex}`}
                      className={`${cl} ${type}-header ${columnClassName || ''} ${className || ''}`}
                      onClick={() => {
                        label !== '' && sortable !== false && handleSort(accessor)
                      }}
                    >
                      <div>
                        {labelParts.length === 1 ? (
                          <span>{label}</span>
                        ) : (
                          <span>
                            {labelParts[0]} / <span>{labelParts[1]}</span>
                          </span>
                        )}
                        {!sortDisabled && sortColumn === accessor && cl !== 'Default' && <Icon name={`Caret${cl}`} className="sort-icon"></Icon>}
                      </div>
                    </th>
                  )
                })}
                {onDelete && <th className={'options-header'}></th>}
                {options && options.length > 0 && <th className={'options-header'}></th>}
              </tr>
            </thead>
            <tbody onMouseMove={ev => handleMouseMove(ev)}>
              {data.map((item: any) => {
                const [keyValue] = getFromAccessor(item, 'text', keyField)
                const rowKey = keyValue || item.publicId || item.id
                return (
                  <tr
                    id={`row-${rowKey}`}
                    key={rowKey}
                    className={`${item.isHidden ? 'hidden' : ''}${item.isFlagged || item.hasFlaggedReply ? ' moderation-flagged-row' : ''}`}
                    onClick={() => handleRowClick(item)}
                    onMouseEnter={() => handleRowMouseEnter(rowKey)}
                    onMouseLeave={() => handleRowMouseEnter(rowKey)}
                  >
                    {onSelectionChange && (
                      <td className="select-cell" onClick={ev => ev.stopPropagation()}>
                        <input
                          type="checkbox"
                          aria-label="Select row"
                          checked={!!selectedKeys?.includes(String(item[keyField]))}
                          onChange={ev => {
                            const key = String(item[keyField])
                            const current = selectedKeys ?? []
                            onSelectionChange(ev.target.checked ? [...current, key] : current.filter(k => k !== key))
                          }}
                        />
                      </td>
                    )}
                    {columns.map(({ accessor, type, override, className, enumName }, columnIndex) => {
                      let cell = ''
                      let tag = undefined
                      let [value, obj] = getFromAccessor(item, type, accessor)

                      if (override !== undefined) {
                        value = override
                      }

                      switch (type) {
                        case 'checkoutType':
                          cell = humanizeEnumValue(value)
                          break

                        case 'enum':
                          tag = <PropertyTag value={value} />
                          break

                        // Event name + a small "ticketed" mark; reads isTicketed / ticketPriceLabel from the row.
                        case 'eventName':
                          tag = (
                            <div className="event-name-cell">
                              <span>{value ?? '-'}</span>
                              {item.isTicketed ? (
                                <span className="event-ticketed-badge" title="Ticketed (paid) event">
                                  {item.ticketPriceLabel ? `Ticket · ${item.ticketPriceLabel}` : 'Ticket'}
                                </span>
                              ) : null}
                            </div>
                          )
                          break

                        case 'refundStatus':
                          tag = <RefundStatusTag status={value} />
                          break

                        case 'genderArray':
                          const all = (Array.isArray(value) ? [...value] : [])
                            .filter(Boolean)
                            .map(v => String(v))
                            .sort((a, b) => a.localeCompare(b))
                          const visibleCount = 2
                          const visible = all.slice(0, visibleCount)
                          const remaining = Math.max(0, all.length - visible.length)

                          tag = (
                            <div className="gender-array" style={{ display: 'flex', gap: '4px', flexWrap: 'nowrap', }}>
                              {visible.map(gender => (
                                <PropertyTag key={gender} value={gender} />
                              ))}
                              {remaining > 0 && <PropertyTag value={`+${remaining} more`} />}
                            </div>
                          )
                          break

                        case 'document':
                          tag = (
                            <div className="full-name">
                              <div>{obj['name']}</div>
                              <div>{DocumentTypeToDescription.has(obj['type']) ? DocumentTypeToDescription.get(obj['type']) : `Unknown document type`} </div>
                              <div>x</div>
                            </div>
                          )
                          break

                        case 'documentType':
                          tag = <img alt={obj['name']} className="document-icon" src={`/images/filetypes/${obj['type'].toLowerCase()}.svg`}></img>
                          break

                        case 'fullNameAndEmail':
                          tag = (
                            <div className="full-name">
                              <div>
                                {obj['firstName']} {obj['lastName']}
                              </div>
                              <div>{obj['email']}</div>
                            </div>
                          )
                          break

                        case 'fullNameAndTelephone':
                          tag = (
                            <div className="full-name" onClick={() => {}}>
                              <div>
                                {obj['firstName']} {obj['lastName']}
                              </div>
                              <div>{formatTelephone(obj['telephone'])}</div>
                            </div>
                          )
                          break

                        case 'niceNameAndEmail':
                          tag = (
                            <div className="full-name">
                              <div>{obj['niceName']}</div>
                              <div>{obj['email']}</div>
                            </div>
                          )
                          break

                        case 'customerNameAndCode':
                          tag = (
                            <div className="customer-name-and-code">
                              <div>{obj['name']}</div>
                              <div>{obj['code']}</div>
                            </div>
                          )
                          break

                        case 'fullAddress':
                          tag = (
                            <div className="full-address">
                              <div>
                                {obj['address']} {obj['address2']}
                              </div>
                              <div>
                                {obj['city']}, {obj['state']} {obj['postalCode']}
                              </div>
                            </div>
                          )
                          break

                        case 'nameAndAddress': {
                          tag = (
                            <div className="name-and-address">
                              <div className="name-and-address-title">
                                <span>{obj?.['name'] ?? '-'}</span>
                                {obj?.['isClosed'] ? <span className="venue-closed-badge">Closed</span> : null}
                              </div>
                              <div>{obj?.['address'] ?? ''}</div>
                            </div>
                          )

                          break
                        }

                        case 'userAvatar':
                          tag = (
                            <Avatar
                              url={value}
                              firstName={obj?.['firstName']}
                              lastName={obj?.['lastName']}
                              onClick={(event) => {
                                event.stopPropagation()
                                setSelectedUser(item.userPublicId || obj?.publicId || item.publicId)
                              }}
                            />
                          )
                          break

                        case 'venueAvatar':
                          tag = (
                            <Avatar
                              url={value}
                              firstName={obj?.['name']}
                              lastName={obj?.['name']}
                              className={'is-venue'}
                              onClick={() => {
                                navigate(`/admin/venues/${obj?.['publicId']}`)
                              }}
                            />
                          )
                          break

                        case 'avatar':
                          tag = <Avatar url={value} firstName={obj?.['firstName']} lastName={obj?.['lastName']} />
                          break

                        case 'jobStatus':
                          tag = <div className={`job-status ${value}`}></div>
                          break

                        case 'thumbnail':
                          if (value !== null && value !== '') {
                            tag = <div style={{ backgroundImage: `url(${value})` }} className="thumbnail"></div>
                          }
                          break

                        case 'interval':
                          cell = (value ?? 0) > 0 ? formatDuration(value) : '-'
                          break

                        case 'number':
                          cell = value ? value.toLocaleString() : '0'
                          break

                        case 'intervalFromNow':
                          cell = formatDuration(dayjs().diff(dayjs(value), 'seconds'))
                          break

                        case 'timeAgo':
                          cell = value ? dayjs(value).fromNow() : '-'
                          break

                        case 'date':
                          cell = value ? dayjs(value).format('MMM DD, YYYY') : '-'
                          break

                        case 'datetime':
                          cell = value ? dayjs(value).format('MMM DD, YYYY [at] hh:mma') : '-'
                          break

                        case 'dayOfWeek':
                          cell = value ? dayjs().day(value).format('dddd') : '-'
                          break

                        case 'coordinates': {
                          const latitude = obj?.['latitude']
                          const longitude = obj?.['longitude']
                          if (latitude != null && longitude != null) {
                            tag = (
                              <div
                                className="coordinates-cell"
                                onClick={(e: React.MouseEvent) => {
                                  e.stopPropagation()
                                  if (onCoordinatesClick) {
                                    onCoordinatesClick(latitude, longitude)
                                  }
                                }}
                              >
                                {formatCoordinates(latitude, longitude)}
                              </div>
                            )
                          } else {
                            cell = '-'
                          }
                          break
                        }

                        case 'reported':
                          tag = <PropertyTag value={value ? 'Reported' : 'NotInterested'} />
                          break

                        case 'flagged':
                          tag = value ? <span className="moderation-flag-badge">Flagged</span> : null
                          cell = value ? '' : '-'
                          break

                        case 'breadcrumbModerationFlags':
                          tag = (
                            <div className="breadcrumb-moderation-flags">
                              {item.isFlagged && (
                                <span className="moderation-flag-badge">Breadcrumb</span>
                              )}
                              {item.hasFlaggedReply && (
                                <span className="moderation-flag-badge moderation-flag-badge--reply">Reply</span>
                              )}
                            </div>
                          )
                          cell = !item.isFlagged && !item.hasFlaggedReply ? '-' : ''
                          break

                        case 'activityModerationFlags':
                          tag = item.isFlagged ? (
                            <span
                              className={`moderation-flag-badge${
                                item.type === 'Reply' ? ' moderation-flag-badge--reply' : ''
                              }`}
                            >
                              {item.type === 'Reply' ? 'Reply' : 'Breadcrumb'}
                            </span>
                          ) : null
                          cell = item.isFlagged ? '' : '-'
                          break

                        case 'markdown':
                          tag = parseMarkdown(value)
                          break

                        case 'reportDownload':
                          const eventEndDate = obj?.['eventEndDate']
                          if (eventEndDate && new Date(eventEndDate) < new Date()) {
                            tag = (
                              <div
                                className="download-icon-container"
                                onClick={(e: React.MouseEvent) => {
                                  e.stopPropagation()
                                  if (props.onReportDownload) {
                                    props.onReportDownload(obj)
                                  }
                                }}
                                style={{ cursor: 'pointer', display: 'inline-block', padding: '8px' }}
                              >
                                <Icon name="Download" className="download-icon" />
                              </div>
                            )
                          } else {
                            cell = '-'
                          }
                          break

                        default:
                          cell = value
                          break
                      }

                      return (
                        <td key={`${accessor}-${columnIndex}`} className={className}>
                          <div>{tag === undefined ? cell : tag}</div>
                        </td>
                      )
                    })}
                    {options && options.length > 0 && (
                      <td className={'actions'}>
                        <div className="options-container">
                          <div className="menu-dropdown">
                            <Icon
                              name="Ellipsis"
                              className="menu-icon"
                              onClick={(e: React.MouseEvent) => {
                                e.stopPropagation()
                                // Close any other open dropdowns
                                document.querySelectorAll('.dropdown-menu.show').forEach(dropdown => {
                                  dropdown.classList.remove('show')
                                })
                                // Toggle this dropdown
                                const dropdown = e.currentTarget.closest('.menu-dropdown')?.querySelector('.dropdown-menu')
                                if (dropdown) {
                                  const isOpen = dropdown.classList.contains('show')
                                  if (isOpen) {
                                    dropdown.classList.remove('show')
                                    setOpenDropdownRow(null)
                                  } else {
                                    dropdown.classList.add('show')
                                    setOpenDropdownRow(rowKey)
                                  }
                                }
                              }}
                            >
                              ⋯
                            </Icon>
                            <div className="dropdown-menu">
                              {options.map((option, index) => (
                                <div
                                  key={index}
                                  className="dropdown-item"
                                  onClick={(e: React.MouseEvent) => {
                                    e.stopPropagation()
                                    option.onClick(item)
                                  }}
                                >
                                  {option.label}
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </td>
                    )}
                    {onDelete && (
                      <td className={'actions'}>
                        <div
                          onClick={e => {
                            e.stopPropagation()
                            onDelete(item)
                          }}
                        >
                          <Icon name="TrashCan" />
                        </div>
                      </td>
                    )}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
      <UserDetailPopup userPublicId={selectedUser || ''} isOpen={!!selectedUser} onClose={() => setSelectedUser(null)} />
    </>
  ) : null
})

export default Table
